import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '../../auth/AuthPage/UserContext';
import { getProfilePhotoSrc, PLACEHOLDER_DATA_URI } from '../../../utils/profilePhoto';
import LoginGate from '../../common/LoginGate/LoginGate';
import AvatarFrame from '../../common/AvatarFrame/AvatarFrame';
import TraderMatch from '../../common/TraderMatch/TraderMatch';
import Icon from '../../common/Icon/Icon';
import './OneSearch.css';

/**
 * FIND TRADERS - the one people-finding tool.
 *
 * Empty box: traders whose binders FIT yours first (two-way fits on
 * top, the gold line says why), then the newest traders on the
 * platform. Typing searches every trader by username. This page
 * absorbed the old "Match my binder" page - /matches redirects here.
 */
function Traders() {
    const { user } = useUser();
    const navigate = useNavigate();
    const [q, setQ] = useState('');
    const [near, setNear] = useState(0);           // 0 = anywhere
    const [nearAvailable, setNearAvailable] = useState(null);
    const [matches, setMatches] = useState(null);  // binder fits (ranked)
    const [browse, setBrowse] = useState(null);    // newest traders
    const [results, setResults] = useState(null);  // username search
    const debounceRef = useRef(null);
    const qRef = useRef(q);
    qRef.current = q;

    // Near me: the server only considers users WITH a saved location
    // inside the radius - traders without one drop out entirely
    const search = async (query, radius) => {
        try {
            const res = await fetch(
                `/api/onesearch/users?q=${encodeURIComponent(query)}${radius ? `&near=${radius}` : ''}`,
                { credentials: 'include' }
            );
            if (res.ok) setResults((await res.json()).users || []);
        } catch { }
    };

    useEffect(() => {
        if (!user) return;
        fetch('/api/suggested/users', { credentials: 'include' })
            .then(r => (r.ok ? r.json() : { users: [] }))
            .then(d => setMatches(d.users || []))
            .catch(() => setMatches([]));
    }, [user]);

    // Newest-traders list follows the radius; an active username
    // search re-runs when the radius moves
    useEffect(() => {
        if (!user) return;
        fetch(`/api/onesearch/users${near ? `?near=${near}` : ''}`, { credentials: 'include' })
            .then(r => (r.ok ? r.json() : { users: [] }))
            .then(d => {
                setBrowse(d.users || []);
                setNearAvailable(!!d.nearAvailable);
            })
            .catch(() => setBrowse([]));
        if (qRef.current.trim()) search(qRef.current.trim(), near);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, near]);

    if (!user) {
        return <LoginGate title="Traders" section="Find people" message="Log in to find traders." />;
    }

    // One row shape for the search + newest lists
    const row = (u) => (
        <li key={u.id} className="os-user-row">
            <AvatarFrame user={u}>
                <img
                    src={getProfilePhotoSrc(u)}
                    alt={u.username}
                    onError={e => { e.target.onerror = null; e.target.src = PLACEHOLDER_DATA_URI; }}
                />
            </AvatarFrame>
            <div className="os-user-main">
                <span className="os-user-name">
                    {u.username}
                    {u.mutualMatch && <span className="os-mutual">two-way fit</span>}
                </span>
                {(u.location || u.haves != null || u.private) && (
                    <span className="os-user-meta">
                        {u.location && <><Icon name="pin" /> {u.location}{u.distance != null ? ` · ${u.distance} mi` : ''} · </>}
                        {u.private
                            ? 'private binder'
                            : u.haves != null ? `${u.haves} in binder · ${u.wants ?? 0} wanted` : ''}
                    </span>
                )}
                {(u.havesToWants > 0 || u.wantsToHaves > 0) && (
                    <span className="os-user-fit">
                        {u.havesToWants > 0 &&
                            `has ${u.havesToWants} card${u.havesToWants !== 1 ? 's' : ''} you want`}
                        {u.havesToWants > 0 && u.wantsToHaves > 0 && ' · '}
                        {u.wantsToHaves > 0 && `wants ${u.wantsToHaves} of yours`}
                    </span>
                )}
            </div>
            <div className="os-user-actions">
                <button className="os-btn ghost" onClick={() => navigate(`/profile/${u.id}`)}>View binder</button>
                <button className="os-btn gold" onClick={() => navigate('/trade', { state: { ownerId: Number(u.id) } })}>Send trade</button>
            </div>
        </li>
    );

    const searching = q.trim().length > 0;
    const matchIds = new Set((matches || []).map(m => String(m.id)));
    const newest = (browse || []).filter(u => !matchIds.has(String(u.id)));
    // Radius on: matches must have a location inside it (the server
    // already enforces this for the search + newest lists)
    const fits = near
        ? (matches || []).filter(m => m.distance != null && m.distance <= near)
        : (matches || []);

    return (
        <div className="onesearch">
            <header className="page-section-head">
                <h1>Traders <span className="page-section-current">Find people</span></h1>
            </header>
            <div className="os-boxwrap">
                <input
                    className="os-box"
                    placeholder="Search traders by username…"
                    value={q}
                    onChange={e => {
                        const v = e.target.value;
                        setQ(v);
                        setResults(null);
                        clearTimeout(debounceRef.current);
                        if (v.trim()) debounceRef.current = setTimeout(() => search(v.trim(), near), 300);
                    }}
                    autoFocus
                />
            </div>

            {/* Near me: slider when a location is saved, otherwise the
                control IS the way to go set one */}
            <div className="os-traders-near">
                {nearAvailable === false ? (
                    <button className="os-setloc-btn" onClick={() => navigate('/account?tab=preferences')}>
                        <Icon name="pin" /> Set your location to unlock local search
                    </button>
                ) : (
                    <>
                        <span className="os-near-label">Near me</span>
                        <input
                            type="range"
                            className="os-range"
                            style={{ '--fill': `${(near / 250) * 100}%` }}
                            min="0" max="250" step="5"
                            value={near}
                            onChange={e => setNear(Number(e.target.value))}
                            aria-label="Near me radius"
                        />
                        <span className="os-range-val">{near ? `${near} mi` : 'Anywhere'}</span>
                    </>
                )}
            </div>

            {searching ? (
                results === null ? (
                    <p className="os-hint">Searching&hellip;</p>
                ) : results.length === 0 ? (
                    <div className="requests-placeholder">No traders matched &ldquo;{q.trim()}&rdquo;.</div>
                ) : (
                    <ul className="os-trade-list">{results.map(row)}</ul>
                )
            ) : matches === null || browse === null ? (
                <p className="os-hint">Finding traders&hellip;</p>
            ) : (
                <>
                    {fits.length > 0 ? (
                        <>
                            <h3 className="os-people-sub">Fits your binder</h3>
                            <p className="os-hint">
                                The trade at a glance: cards you'd get, cards you'd give.
                                Two-way fits first, since those trades are the likeliest to happen.
                            </p>
                            <div className="os-match-grid">{fits.map(m => <TraderMatch key={m.id} m={m} />)}</div>
                        </>
                    ) : matches.length > 0 && near ? (
                        <p className="os-hint">
                            No binder fits within {near} mi. Slide the radius out to see all {matches.length}.
                        </p>
                    ) : (
                        <p className="os-hint">
                            No binder fits yet. Add cards to your <Link to="/binders">binder and wishlist</Link> and
                            traders whose cards line up with yours will appear here.
                        </p>
                    )}
                    {newest.length > 0 && (
                        <>
                            <h3 className="os-people-sub">New traders</h3>
                            <ul className="os-trade-list">{newest.map(row)}</ul>
                        </>
                    )}
                </>
            )}
        </div>
    );
}

export default Traders;
